import type { ActivityEntry, Address, AuditFields, PrincipalRef, Priority, Resolution, SlaState, TimestampLike } from './common';

export const COMPANY_TYPES = ['customer', 'partner', 'prospect', 'internal'] as const;
export const COMPANY_STATUSES = ['active', 'onboarding', 'archived'] as const;
export const COMPANY_TIERS = ['bronze', 'silver', 'gold', 'platinum'] as const;
export const ACCOUNT_TEAM_ROLES = ['account_manager', 'support_agent', 'field_tech', 'ai_agent'] as const;
export type AccountTeamRole = (typeof ACCOUNT_TEAM_ROLES)[number];
export interface AccountTeamMember {
  principal: PrincipalRef;
  role: AccountTeamRole;
}
export interface SpecialHandlingNote {
  text: string;
  severity: 'info' | 'warning' | 'critical';
  active: boolean;
}
export interface Company extends AuditFields {
  id: string; // ACC0001
  name: string;
  code: string;
  type: (typeof COMPANY_TYPES)[number];
  status: (typeof COMPANY_STATUSES)[number];
  parentCompanyId: string | null;
  industry: string;
  tier: (typeof COMPANY_TIERS)[number];
  website: string;
  domains: string[];
  phone: string;
  address: Address | null;
  timezone: string;
  accountManagerUid: string | null;
  accountTeam: AccountTeamMember[];
  slaPolicyId: string | null;
  primaryContactId: string | null;
  specialHandlingNotes: SpecialHandlingNote[];
  tags: string[];
  openCaseCount: number;
}
export interface CompanyActivity {
  id: string;
  type: 'note' | 'call' | 'email' | 'meeting' | 'system';
  body: string;
  by: PrincipalRef;
  at: TimestampLike;
}

export interface Contact extends AuditFields {
  id: string; // CON0001
  companyId: string;
  name: string;
  email: string;
  phone: string;
  mobile: string;
  title: string;
  isPrimary: boolean;
  status: 'active' | 'inactive';
  portalAccess: boolean;
  notes: string;
}

export const CONTRACT_TYPES = ['support', 'managed_services', 'project', 'warranty'] as const;
export interface Contract extends AuditFields {
  id: string; // CTR0001
  companyId: string;
  name: string;
  type: (typeof CONTRACT_TYPES)[number];
  startDate: string; // ISO date
  endDate: string; // ISO date
  status: 'draft' | 'active' | 'expired' | 'cancelled';
  slaPolicyId: string | null;
  entitlements: {
    supportHours: '8x5' | '24x7';
    channels: string[];
    includedServices: string[];
    responseCredits: number;
  };
  coveredAssetIds: string[];
  value: number;
}

export const CASE_STATES = ['New', 'Open', 'Awaiting Info', 'Resolved', 'Closed', 'Cancelled'] as const;
export type CaseState = (typeof CASE_STATES)[number];
export const CASE_CHANNELS = ['phone', 'email', 'portal', 'chat', 'agent'] as const;
export interface RelatedRecords {
  incidents: string[];
  problems: string[];
  changes: string[];
  workOrders: string[];
  articles: string[];
}
export interface Case extends AuditFields {
  id: string; // CS0001001
  companyId: string;
  contactId: string | null;
  contractId: string | null;
  short: string;
  description: string;
  category: string;
  subcategory: string;
  priority: Priority;
  impact: 1 | 2 | 3;
  urgency: 1 | 2 | 3;
  state: CaseState;
  channel: (typeof CASE_CHANNELS)[number];
  assignedGroup: string | null;
  assignedTo: PrincipalRef | null;
  sla: SlaState;
  related: RelatedRecords;
  resolution: Resolution | null;
  specialHandlingSnapshot: SpecialHandlingNote[];
  tags: string[];
  source: { agentRunId?: string; autoTriageAgentId?: string };
}
export type CaseActivity = ActivityEntry;

export const WORK_ORDER_STATES = ['Draft', 'Assigned', 'Dispatched', 'Work In Progress', 'On Hold', 'Closed Complete', 'Closed Incomplete', 'Cancelled'] as const;
export type WorkOrderState = (typeof WORK_ORDER_STATES)[number];
export interface WorkOrder extends AuditFields {
  id: string; // WO0001
  caseId: string | null;
  incidentId: string | null;
  companyId: string;
  contactId: string | null;
  siteAddress: Address | null;
  siteLocation?: string; // Work-order site label; company address is not an assumed service site
  siteAvailability?: import('../dispatch-availability').DispatchAvailability;
  short: string;
  description: string;
  priority: Priority;
  state: WorkOrderState;
  assignedTechUid: string | null;
  scheduledStart: TimestampLike | null;
  scheduledEnd: TimestampLike | null;
  actualStart: TimestampLike | null;
  actualEnd: TimestampLike | null;
  skillsRequired: string[];
  partsUsed: { name: string; qty: number }[];
  travelNotes: string;
  signoff: { name: string; at: TimestampLike; notes: string } | null;
}
export interface WorkOrderTask {
  id: string;
  short: string;
  state: 'Pending' | 'Done' | 'Skipped';
  assignedTechUid: string | null;
  order: number;
  notes: string;
}

export interface SlaTargets {
  P1: { responseMins: number; resolveMins: number };
  P2: { responseMins: number; resolveMins: number };
  P3: { responseMins: number; resolveMins: number };
  P4: { responseMins: number; resolveMins: number };
}
export interface SlaPolicy {
  id: string; // slug
  name: string;
  default: boolean;
  targets: SlaTargets;
  businessHours: { tz: string; days: number[]; start: string; end: string } | '24x7';
  pauseOnStates: string[];
}

export interface AssignmentRule {
  id: string;
  order: number;
  active: boolean;
  appliesTo: 'case' | 'incident';
  conditions: { companyIds?: string[]; categories?: string[]; priorities?: Priority[]; keywords?: string[] };
  action: { group?: string; principal?: PrincipalRef; autoTriageAgentId?: string };
}
